import { compose, withHandlers, withStateHandlers } from "recompose";

import { graphql } from "@apollo/client/react/hoc";

import { withRouter } from "react-router-dom";

import { MOVE_ANIMALS_TO_FIELDS } from "api/MyLivestock/Fields/mutations";
import { GET_ANIMAL_BY_ID, GET_MY_ANIMALS_LIST } from "api/MyLivestock/Animal/queries";
import { GET_ANIMAL_HISTORY_BY_ID } from "api/MyLivestock/Activity/queries";

import { getBUFromLocalStorage } from "helpers/storage";

import ConfirmMoveToFieldModal from "./ConfirmMoveToFieldModal";
import { Species } from "state";

const moveAnimalToField =
  ({ match, animals, setRequesting, setError, assignAnimals, onClose, selectAnimal, isRefetchSingleAnimalInfo }) =>
  async (props, showNotification, fieldsData, speciesActive?: Species): Promise<void> => {
    if (fieldsData.length === 0) {
      setError("You have no location to move animal(s) to.");
      return;
    }

    setRequesting(true);
    setError(null);

    const businessUnitId = getBUFromLocalStorage();
    const animalsMessageWord = `animal${animals.length > 1 ? "s" : ""}`;

    try {
      const {
        data: {
          moveAnimalsToField: { errors },
        },
      } = await assignAnimals({
        variables: {
          input: {
            field: props.field.length > 0 ? props.field : null,
            animals,
            businessUnit: +businessUnitId,
          },
        },
        refetchQueries: () => {
          if (isRefetchSingleAnimalInfo) {
            return [
              {
                query: GET_ANIMAL_BY_ID,
                variables: {
                  id: animals[0],
                },
              },
              {
                query: GET_ANIMAL_HISTORY_BY_ID,
                variables: {
                  first: 5,
                  after: "",
                  animal: match.params.id,
                  activityTemplateSlugs: [],
                  activityTypeSlugs: [],
                },
              },
            ];
          }

          return [];
        },
        update: (
          store,
          {
            data: {
              moveAnimalsToField: { success },
            },
          },
        ) => {
          if (!success || !isRefetchSingleAnimalInfo) {
            return;
          }
          const previousVariables = {
            businessUnitId,
            animalTypeIds: speciesActive?.id ? [Number(speciesActive?.id)] : undefined,
          };
          const field = fieldsData.find((el) => el.id === props.field);

          const data = store.readQuery({
            query: GET_MY_ANIMALS_LIST,
            variables: previousVariables,
          });

          const newAnimals = data.animalsExtended.animals.map((item) => {
            if (animals.indexOf(item.id) > -1) {
              return {
                ...item,
                field,
              };
            }

            return item;
          });
          store.writeQuery({
            query: GET_MY_ANIMALS_LIST,
            variables: previousVariables,
            data: {
              ...data,
              animalsExtended: {
                ...data.animalsExtended,
                animals: newAnimals,
              },
            },
          });
        },
      });
      setRequesting(false);

      if (errors) {
        setError(errors[0].message);
        showNotification({
          variant: "error",
          message: `Error moving ${animalsMessageWord}.`,
        });
      } else {
        showNotification({
          message: `${animalsMessageWord.charAt(0).toUpperCase()}${animalsMessageWord.slice(1)} successfully moved.`,
        });
        onClose();
        if (selectAnimal) {
          selectAnimal([]);
        }
      }
    } catch (e) {
      showNotification({
        variant: "error",
        message: `Error moving ${animalsMessageWord}.`,
      });

      setRequesting(false);
      setError(`Error moving ${animalsMessageWord}.`);
    }
  };

export default compose(
  withRouter,
  graphql(MOVE_ANIMALS_TO_FIELDS, {
    name: "assignAnimals",
  }),
  withStateHandlers(
    {
      error: null,
      requesting: false,
    },
    {
      setError:
        () =>
        (error): Record<string, unknown> => ({
          error,
        }),
      setRequesting:
        () =>
        (requesting): Record<string, unknown> => ({
          requesting,
        }),
    },
  ),
  withHandlers({
    moveAnimalToField,
  }),
)(ConfirmMoveToFieldModal);
